// "Contagem ao vivo": quantos clientes a escolha de público alcança. Usa a listagem de clientes (só o total).
// Etiqueta e grupo valem como "ou" (orFilters); cidade é uma trava a mais. Só cidade (todos os clientes dessas
// cidades) sai das contas da rede (customer-city-stats), sem consulta.
import { useEffect, useState } from 'react'
import { useDataProvider } from 'react-admin'
import { useQuery } from '@tanstack/react-query'
import { cityCustomers } from '../model'
import type { CityStats } from '../types'

export interface CountFilter {
  tagIds: string[]
  groupIds: string[]
  cityIds: string[]
}

export interface CountResult {
  count: number | null
  loading: boolean
  failed: boolean
  /** false quando a trava de cidade não pôde entrar na conta: o número é um teto. */
  exact: boolean
}

/** As contas de cidade da rede: `pending` enquanto não chegaram nem falharam. */
export interface StatsInput {
  data: CityStats | null
  pending: boolean
}

const MAX_CITY_QUERIES = 5

/** Só dispara depois de uma pausa na digitação/seleção: nada de uma consulta por clique. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return v
}

/**
 * `filter` null = todos os clientes da rede. Com filtro, conta quem tem alguma das etiquetas OU está em algum
 * dos grupos (e, se houver cidades, mora numa delas). Só cidades: soma dos clientes de cada cidade.
 */
export function useAudienceCount(chainId: string, filter: CountFilter | null, enabled: boolean, stats?: StatsInput): CountResult {
  const dataProvider = useDataProvider()
  const key = JSON.stringify(filter)
  const debouncedKey = useDebounced(key, 500)
  const settled = debouncedKey === key
  const f: CountFilter | null = JSON.parse(debouncedKey)
  const hasWho = !!f && (f.tagIds.length > 0 || f.groupIds.length > 0)
  const cities = f ? f.cityIds : []
  const useCities = cities.length > 0 && cities.length <= MAX_CITY_QUERIES
  const cityOnly = !!f && !hasWho && cities.length > 0
  const statsSum = cityCustomers(stats?.data, cities)
  const waitingStats = !!stats?.pending && statsSum == null

  // Só cidade e as contas da rede já chegaram: soma direta, sem consulta. Sem contas (erro), cai nas consultas por cidade.
  const fromStats = cityOnly && statsSum != null
  const needsQuery = f === null || hasWho || (cityOnly && statsSum == null && !waitingStats && useCities)
  const active = enabled && !!chainId && needsQuery

  const q = useQuery({
    queryKey: ['discountV2', chainId, 'count', debouncedKey],
    // O filtro vem da chave com atraso: enquanto a escolha de agora não assentou, não consulta com a de antes.
    enabled: active && settled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<number> => {
      const total = async (filterParam: Record<string, unknown>) => {
        const res = await dataProvider.getList('customers', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: filterParam })
        return Number(res.total) || 0
      }
      if (f === null) return total({})
      const orFilters: Record<string, string[]> = {}
      if (f.tagIds.length) orFilters.tagIds = f.tagIds
      if (f.groupIds.length) orFilters.groupIds = f.groupIds
      const who = hasWho ? { orFilters } : {}
      if (!useCities) return total(who)
      // cada cliente mora numa cidade só, então a soma por cidade não conta ninguém duas vezes
      const parts = await Promise.all(cities.map(cityId => total({ ...who, cityId })))
      return parts.reduce((a, b) => a + b, 0)
    },
  })

  if (fromStats && enabled) {
    return { count: statsSum, loading: false, failed: false, exact: true }
  }
  // Etiqueta ou grupo com muitas cidades: o teto é o menor entre "tem a etiqueta" e "mora nessas cidades".
  const tooManyCities = hasWho && cities.length > MAX_CITY_QUERIES
  const queried = active && q.data != null ? q.data : null
  const capped = queried != null && tooManyCities && statsSum != null ? Math.min(queried, statsSum) : queried
  return {
    count: capped,
    loading: enabled && !!chainId && (waitingStats || (needsQuery && (!settled || q.isFetching) && q.data == null)),
    failed: active && q.isError,
    exact: !tooManyCities && (cities.length === 0 || useCities || fromStats),
  }
}
